import * as React from 'react';
import {
  Box,
  Sheet,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  Typography,
  IconButton,
} from '@mui/joy';
import { useTheme } from '@mui/joy/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { X as CloseIcon } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

// ✅ Nuove importazioni
import { useOrg } from '../utils/OrgContext';
import { getNavItems, filterNavItemsByPermissions, isSuperadmin } from '../utils/permissions';

export default function Sidebar({ open, setOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const { selectedOrg } = useOrg(); // ✅ ente selezionato dal context
  const [filteredNavItems, setFilteredNavItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      const superadmin = isSuperadmin();
      const allItems = getNavItems(selectedOrg); // ✅ genera nav dinamici
      const items = superadmin ? allItems : filterNavItemsByPermissions(allItems);
      setFilteredNavItems(items);
      setLoading(false);
    }, 100); // puoi ridurre il delay se vuoi

    return () => clearTimeout(timer);
  }, [selectedOrg]);

  const handleNavigate = (path) => {
    navigate(path);
    if (isSmallScreen) setOpen(false);
  };

  if (loading) return null;

  return (
    <Sheet
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: 250,
        height: '100vh',
        bgcolor: 'background.body',
        borderRight: '1px solid',
        borderColor: 'divider',
        p: 2,
        zIndex: 1200,
        transition: 'transform 0.3s ease-in-out',
        transform: {
          xs: open ? 'translateX(0)' : 'translateX(-100%)',
          md: 'translateX(0)',
        },
      }}
    >
      {isSmallScreen && open && (
        <IconButton
          onClick={() => setOpen(false)}
          aria-label="Chiudi menu"
          size="sm"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            bgcolor: 'background.surface',
            boxShadow: 'md',
            zIndex: 1300,
            borderRadius: '50%',
          }}
        >
          <CloseIcon size={20} />
        </IconButton>
      )}

      {/* Intestazione */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <img
          src={`${process.env.REACT_APP_SERVER_HOST}/assets/img/logo/SEM.svg`}
          alt="SEM Logo"
          style={{ width: 32, height: 32 }}
        />
        <Typography level="title-lg" noWrap>
          SEM - Admin
        </Typography>
      </Box>

      <Box sx={{ height: '20px' }} />

      {/* Navigazione */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <List size="sm" sx={{ gap: 1 }}>
          {filteredNavItems.map(({ label, icon: Icon, path }) => {
            const isActive = location.pathname === path;
            return (
              <ListItem key={path}>
                <ListItemButton
                  selected={isActive}
                  onClick={() => handleNavigate(path)}
                  sx={{
                    borderRadius: '12px',
                    gap: 1.5,
                    minHeight: 37,
                    px: 2,
                    py: 1,
                    transition: 'background-color 0.2s ease',

                    '&:hover': {
                      bgcolor: 'neutral.softHoverBg',
                    },

                    '&:hover .nav-icon': {
                      transform: 'scale(1.15) rotate(-8deg)',
                    },
                  }}
                >
                  <Box
                    className="nav-icon"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'transform 0.2s ease',
                    }}
                  >
                    <Icon size={20} strokeWidth={1.5} />
                  </Box>
                  <ListItemContent>
                    <Typography level="title-sm">{label}</Typography>
                  </ListItemContent>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Sheet>
  );
}
